import React, { useState } from 'react';
import { Button } from './Button';
import { FileDropzone } from './FileDropzone';
import { useApi } from '../hooks/useApi';
import { CheckCircle, UploadSimple, Warning } from 'phosphor-react';
import { Loader } from './Loader';

interface ImportResult {
  listsUpserted: number;
  itemsUpserted: number;
  skippedSkuNotFound: number;
  skippedDedup?: number;
  notFoundSkus: string[];
  parseErrors: string[];
}

interface PriceListImportSectionProps {
  onImportSuccess?: () => void;
}

export const PriceListImportSection: React.FC<PriceListImportSectionProps> = ({ onImportSuccess }) => {
  const api = useApi();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFilesSelected = (files: File[]) => {
    setSelectedFile(files[0] ?? null);
    setResult(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    setResult(null);
    setError(null);
    try {
      const summary = await api.importPriceListCsv(selectedFile);
      setResult(summary);
      setSelectedFile(null);
      onImportSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-sans font-semibold text-xl text-foreground">
          Importar Listas de Precios
        </h2>
        <p className="font-sans text-sm text-text-subtle mt-1">
          Sube el CSV exportado desde Kameone para actualizar las listas de precios
        </p>
      </div>

      {/* Format info */}
      <div className="border border-border rounded-lg p-4 bg-muted/20">
        <h4 className="font-sans font-semibold text-sm text-foreground mb-2">
          Formato esperado del CSV
        </h4>
        <p className="font-sans text-xs text-text-subtle mb-1">
          Encabezado requerido (primera fila):
        </p>
        <code className="block font-mono text-xs bg-muted px-2 py-1 rounded text-foreground">
          Lista de Precio, Artículo, SKU, Precio de Venta Neto
        </code>
        <ul className="mt-3 space-y-1 font-sans text-xs text-text-subtle">
          <li>• Los precios se actualizan si ya existen (upsert)</li>
          <li>• Los productos se identifican por SKU — deben existir en el catálogo</li>
          <li>• La asignación de clientes a listas se guarda al seleccionar manualmente en el pedido</li>
        </ul>
      </div>

      {/* Dropzone */}
      <FileDropzone
        onFilesSelected={handleFilesSelected}
        acceptedFileTypes={{ 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] }}
        maxFiles={1}
        label="Arrastra el CSV aquí o haz clic para seleccionar"
        labelActive="Suelta aquí"
        showSelectedFiles={true}
      />

      {/* Upload button */}
      {selectedFile && (
        <Button
          onClick={handleUpload}
          disabled={isUploading}
          className="gap-2"
        >
          {isUploading ? (
            <>
              <Loader />
              Importando...
            </>
          ) : (
            <>
              <UploadSimple size={16} weight="bold" />
              Importar
            </>
          )}
        </Button>
      )}

      {/* Success result */}
      {result && (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6">
          <div className="flex gap-4">
            <CheckCircle size={24} weight="fill" className="text-green-600 shrink-0 mt-1" />
            <div className="w-full">
              <h3 className="font-sans font-semibold text-base text-green-800 mb-3">
                Importación completada
              </h3>
              <ul className="space-y-1 font-sans text-sm text-green-700">
                <li>Listas creadas/actualizadas: <strong>{result.listsUpserted}</strong></li>
                <li>Precios actualizados: <strong>{result.itemsUpserted}</strong></li>
                {(result.skippedDedup ?? 0) > 0 && (
                  <li className="text-orange-700">
                    Filas duplicadas ignoradas: <strong>{result.skippedDedup}</strong>
                  </li>
                )}
                {result.skippedSkuNotFound > 0 && (
                  <li className="text-orange-700">
                    SKUs no encontrados en catálogo: <strong>{result.skippedSkuNotFound}</strong>
                    {result.notFoundSkus.length > 0 && (
                      <span className="block font-mono text-xs mt-1 text-orange-600">
                        {result.notFoundSkus.join(', ')}
                      </span>
                    )}
                  </li>
                )}
              </ul>
              {result.parseErrors.length > 0 && (
                <div className="mt-3">
                  <p className="font-sans text-xs font-semibold text-orange-700">Filas ignoradas:</p>
                  <ul className="list-disc list-inside font-sans text-xs text-orange-600 mt-1 space-y-0.5">
                    {result.parseErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
          <div className="flex gap-4">
            <Warning size={24} weight="fill" className="text-red-600 shrink-0 mt-1" />
            <div>
              <h3 className="font-sans font-semibold text-base text-red-800 mb-1">
                Error en la importación
              </h3>
              <p className="font-sans text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
